import React, { useState, useEffect, useCallback } from 'react';
import { validateEnv } from './utils/envCheck';
import LandingPage from './components/LandingPage';
import OnboardingWizard from './components/OnboardingWizard';
import Dashboard from './components/Dashboard';
import ProductionDashboard from './components/ProductionDashboard';
import InternalAnalytics from './components/InternalAnalytics';
import AdminWorkspace from './components/AdminWorkspace';
import AdminAnalyticsDashboard from './components/analytics/AdminAnalyticsDashboard';
import LoginModal from './components/LoginModal';
import ResetPasswordPage from './components/ResetPasswordPage';
import PrivacyPage from './components/PrivacyPage';
import AboutUsPage from './components/AboutUsPage';
import ProductDescriptionPage from './components/ProductDescriptionPage';
import DevCommandCenter from './components/dev-center/DevCommandCenter';
import { ThemeProvider } from './contexts/ThemeContext';
import { isAdmin, loadFeatureFlags, requestStagingReviewSession, shouldUseStagingReviewSession } from './services/api';
import { initSessionTracking, trackViewChange } from './utils/telemetry';
import UpgradeActivationBanner from './components/UpgradeActivationBanner';

// ── Route helpers ────────────────────────────────────────────────────────────

/** Derive the correct view from the current URL pathname. */
const viewFromPath = (pathname, hasProfile) => {
  if (pathname === '/privacy') return 'privacy';
  if (pathname === '/about-us') return 'about-us';
  if (pathname === '/product-description' || pathname === '/product') return 'product';
  if (pathname.startsWith('/admin/dev-center')) return 'dev-center';
  if (pathname.startsWith('/admin')) return 'admin-workspace';
  return hasProfile ? 'dashboard' : 'landing';
};

const shouldBootstrapStagingReview = () => {
  try {
    if (!shouldUseStagingReviewSession()) return false;
    const path = window.location.pathname;
    return !(
      path === '/privacy' ||
      path === '/about-us' ||
      path === '/product' ||
      path === '/product-description' ||
      path === '/reset-password'
    );
  } catch {
    return false;
  }
};

const AppContent = () => {
  const [userProfile, setUserProfile] = useState(() => {
    try {
      const saved = localStorage.getItem('saige_profile');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  // Experience mode: 'PRODUCTION' or 'ASSISTANT'
  const [experienceMode, setExperienceMode] = useState(() => {
    try {
      const saved = localStorage.getItem('saige_experience_mode');
      return saved || 'PRODUCTION';
    } catch {
      return 'PRODUCTION';
    }
  });

  // Assistant mode is gated — only accessible via ?mode=assistant URL param
  // Once unlocked, it persists in localStorage
  const [assistantUnlocked] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('mode') === 'assistant') {
        localStorage.setItem('saige_assistant_unlocked', 'true');
        // Clean up URL
        params.delete('mode');
        const remaining = params.toString();
        window.history.replaceState({}, '', remaining ? `/?${remaining}` : '/');
        return true;
      }
      return localStorage.getItem('saige_assistant_unlocked') === 'true';
    } catch {
      return false;
    }
  });

  const [view, setView] = useState(() =>
    viewFromPath(window.location.pathname, !!localStorage.getItem('saige_profile'))
  );
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [passwordToken, setPasswordToken] = useState(null);
  const [isPasswordReset, setIsPasswordReset] = useState(false);
  const [showInternalAnalytics, setShowInternalAnalytics] = useState(false);
  const [isStagingReviewBootstrapping, setIsStagingReviewBootstrapping] = useState(shouldBootstrapStagingReview);

  // Phase 3B: post-upgrade activation
  const [upgradeInfo, setUpgradeInfo] = useState(null);

  // Save experience mode to localStorage
  useEffect(() => {
    localStorage.setItem('saige_experience_mode', experienceMode);
  }, [experienceMode]);

  // Environment validation — logs warnings on mount, never throws
  useEffect(() => {
    validateEnv();
    initSessionTracking();
    loadFeatureFlags(); // Phase 3A — non-blocking
  }, []);

  useEffect(() => {
    if (!shouldBootstrapStagingReview()) return undefined;

    let cancelled = false;
    const path = window.location.pathname;
    const reviewRole = path.startsWith('/admin') ? 'district_admin' : 'teacher';

    const bootstrap = async () => {
      setIsStagingReviewBootstrapping(true);
      try {
        const profile = await requestStagingReviewSession(reviewRole);
        if (cancelled) return;

        setUserProfile(profile);
        setView(viewFromPath(window.location.pathname, true));
        loadFeatureFlags();
      } catch (error) {
        console.warn('[Saige] Staging review session unavailable', error?.message || error);
      } finally {
        if (!cancelled) {
          setIsStagingReviewBootstrapping(false);
        }
      }
    };

    bootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  // Check URL parameters on load (password setup or password reset)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const setup = params.get('setup');
    const token = params.get('token');
    const path = window.location.pathname;

    if (setup === 'password' && token) {
      // Email verification → set initial password
      setPasswordToken(token);
      setIsPasswordReset(false);
      setView('set-password');
      window.history.replaceState({}, '', '/');
    } else if (path === '/reset-password' && token) {
      // Forgot password → reset password
      setPasswordToken(token);
      setIsPasswordReset(true);
      setView('set-password');
      window.history.replaceState({}, '', '/');
    } else if (path === '/privacy') {
      setView('privacy');
    } else if (path === '/about-us') {
      setView('about-us');
    } else if (path === '/product-description' || path === '/product') {
      if (path === '/product-description') {
        window.history.replaceState({}, '', '/product');
      }
      setView('product');
    } else if (path.startsWith('/admin/dev-center') && userProfile) {
      // Dev Command Center — standalone route, does NOT require isAdmin()
      setView('dev-center');
    } else if (path.startsWith('/admin') && userProfile && isAdmin()) {
      // Route-based admin workspace — preserve URL on refresh
      setView('admin-workspace');
    } else if (params.get('view') === 'internal-analytics' && userProfile && isAdmin()) {
      setShowInternalAnalytics(true);
      params.delete('view');
      const remaining = params.toString();
      window.history.replaceState({}, '', remaining ? `/?${remaining}` : '/');
    }

    // Phase 3B: Detect post-checkout return
    if (params.get('subscription') === 'success') {
      const plan = params.get('plan') || 'Pro';
      const source = params.get('source') || 'direct';
      const newPlanDisplay = plan.charAt(0).toUpperCase() + plan.slice(1);

      // Update localStorage profile with new plan tier
      try {
        const profile = JSON.parse(localStorage.getItem('saige_profile') || '{}');
        if (profile) {
          profile.plan_tier = newPlanDisplay;
          localStorage.setItem('saige_profile', JSON.stringify(profile));
          setUserProfile({ ...profile });
        }
      } catch { /* silent */ }

      setUpgradeInfo({ plan: newPlanDisplay, source });

      // Clean up URL
      params.delete('subscription');
      params.delete('plan');
      params.delete('source');
      const remaining = params.toString();
      window.history.replaceState({}, '', remaining ? `/?${remaining}` : '/');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen to browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname;
      if (path.startsWith('/admin/dev-center') && userProfile) {
        setView('dev-center');
      } else if (path.startsWith('/admin') && userProfile && isAdmin()) {
        setView('admin-workspace');
      } else if (path === '/privacy') {
        setView('privacy');
      } else if (path === '/about-us') {
        setView('about-us');
      } else if (path === '/product-description' || path === '/product') {
        setView('product');
      } else {
        setView(userProfile ? 'dashboard' : 'landing');
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [userProfile]);

  // Effect to sync state with view — skip if on admin or special path
  useEffect(() => {
    if (userProfile && view !== 'set-password' && view !== 'admin-workspace' && view !== 'dev-center' && view !== 'privacy' && view !== 'about-us' && view !== 'product') {
      setView('dashboard');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile]);

  // Track view transitions for analytics
  useEffect(() => {
    trackViewChange(view);
  }, [view]);

  const handleStartOnboarding = () => {
    setView('onboarding');
  };

  const handleLogin = () => {
    setShowLoginModal(true);
  };

  const handleLoginSuccess = (profile) => {
    localStorage.setItem('saige_profile', JSON.stringify(profile));
    setUserProfile(profile);
    setView('dashboard');
    setShowLoginModal(false);
    // Phase 3.2: Re-fetch feature flags with auth so user-aware flags (lesson_chat)
    // are resolved correctly. Token is now in localStorage; axios interceptor sends it.
    loadFeatureFlags();
  };

  const handleOnboardingComplete = (profileData) => {
    localStorage.setItem('saige_profile', JSON.stringify(profileData));
    setUserProfile(profileData);
    setView('dashboard');
  };

  const handleLogout = () => {
    localStorage.removeItem('saige_profile');
    localStorage.removeItem('saige_token');
    // Phase 3.2: Clear flag cache so the next user on this device gets a fresh
    // (unauthenticated) flag resolution rather than inheriting a stale allowlist hit.
    localStorage.removeItem('saige_feature_flags');
    setUserProfile(null);
    setView('landing');
    setShowLoginModal(true);
    // Clear any admin route from URL
    if (window.location.pathname !== '/') {
      window.history.replaceState({}, '', '/');
    }
  };

  const handlePasswordSetSuccess = () => {
    setPasswordToken(null);
    setView('landing');
    setShowLoginModal(true);
  };

  // Navigate to the route-based admin workspace
  const handleEnterAdminWorkspace = useCallback(() => {
    window.history.pushState({}, '', '/admin');
    setView('admin-workspace');
  }, []);

  // Navigate to the Dev Command Center
  const handleEnterDevCenter = useCallback(() => {
    window.history.pushState({}, '', '/admin/dev-center');
    setView('dev-center');
  }, []);

  // Leave admin workspace — go back to teacher dashboard
  const handleLeaveAdminWorkspace = useCallback(() => {
    window.history.pushState({}, '', '/');
    setView('dashboard');
  }, []);

  // Mode switching handlers — only functional when assistant is unlocked
  const switchToAssistant = () => {
    if (assistantUnlocked) setExperienceMode('ASSISTANT');
  };
  const switchToProduction = () => setExperienceMode('PRODUCTION');

  if (isStagingReviewBootstrapping) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center">
        <div className="text-sm font-medium tracking-wide text-slate-300">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 transition-colors">
      {view === 'dev-center' && userProfile && (
        <DevCommandCenter onBack={() => {
          window.history.pushState({}, '', '/');
          setView('dashboard');
        }} />
      )}

      {view === 'dev-center' && !userProfile && (() => {
        window.history.replaceState({}, '', '/');
        setView('landing');
        return null;
      })()}

      {view === 'set-password' && passwordToken && (
        <ResetPasswordPage
          token={passwordToken}
          isReset={isPasswordReset}
          onSuccess={handlePasswordSetSuccess}
        />
      )}

      {view === 'about-us' && (
        <AboutUsPage
          onBack={() => {
            window.history.pushState({}, '', '/');
            setView(userProfile ? 'dashboard' : 'landing');
          }}
          onGetStarted={handleStartOnboarding}
        />
      )}

      {view === 'product' && (
        <ProductDescriptionPage
          onBack={() => {
            window.history.pushState({}, '', '/');
            setView(userProfile ? 'dashboard' : 'landing');
          }}
          onGetStarted={handleStartOnboarding}
        />
      )}

      {view === 'privacy' && (
        <PrivacyPage onBack={() => {
          window.history.pushState({}, '', '/');
          setView(userProfile ? 'dashboard' : 'landing');
        }} />
      )}

      {view === 'landing' && (
        <LandingPage
          onGetStarted={handleStartOnboarding}
          onLogin={handleLogin}
          onAboutUs={() => {
            window.history.pushState({}, '', '/about-us');
            setView('about-us');
          }}
          onProduct={() => {
            window.history.pushState({}, '', '/product');
            setView('product');
          }}
        />
      )}

      {view === 'onboarding' && (
        <OnboardingWizard
          onComplete={handleOnboardingComplete}
          onBack={() => setView('landing')}
          onLoginInstead={() => { setView('landing'); setShowLoginModal(true); }}
        />
      )}

      {view === 'dashboard' && userProfile && experienceMode === 'PRODUCTION' && (
        <>
          {/* Phase 3B: Post-upgrade activation banner */}
          {upgradeInfo && (
            <div style={{ maxWidth: 900, margin: '0 auto', padding: '16px 24px 0' }}>
              <UpgradeActivationBanner
                newPlan={upgradeInfo.plan}
                source={upgradeInfo.source}
                onDismiss={() => setUpgradeInfo(null)}
                onAction={() => {
                  setUpgradeInfo(null);
                  // Actions are handled by ProductionDashboard through normal flows
                }}
              />
            </div>
          )}
          <ProductionDashboard
            userProfile={userProfile}
            onSwitchToAssistant={assistantUnlocked ? switchToAssistant : null}
            onLogout={handleLogout}
            onEnterAdminWorkspace={handleEnterAdminWorkspace}
            onEnterDevCenter={handleEnterDevCenter}
          />
        </>
      )}

      {showInternalAnalytics && userProfile && (
        <InternalAnalytics onBack={() => setShowInternalAnalytics(false)} />
      )}

      {view === 'admin-workspace' && userProfile && isAdmin() && (
        <AdminWorkspace onBack={handleLeaveAdminWorkspace} />
      )}

      {/* Non-admin on /admin path — show access-denied screen */}
      {view === 'admin-workspace' && (!userProfile || !isAdmin()) && (
        <AdminAnalyticsDashboard onBack={() => { window.history.replaceState({}, '', '/'); window.location.reload(); }} />
      )}

      {view === 'dashboard' && userProfile && experienceMode === 'ASSISTANT' && (
        <Dashboard
          userProfile={userProfile}
          onLogout={handleLogout}
          onUpdateProfile={(updated) => setUserProfile(updated)}
          onSwitchToProduction={switchToProduction}
        />
      )}

      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLoginSuccess={handleLoginSuccess}
        onSignUp={handleStartOnboarding}
      />
    </div>
  );
};

import ErrorBoundary from './components/ErrorBoundary';

const App = () => (
  <ErrorBoundary>
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  </ErrorBoundary>
);

export default App;
